import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Avatar,
  ButtonDS,
  CritAir,
  EnergyClass,
  RemoveRedEyeOutlined,
  SidePanelDS,
  Spacer,
  Tag,
  TextCapitalized,
  dateFormatterDayMonthYear,
} from "@qivia/ui";
import { useAppSelector } from "../../../../redux/hooks";
import Car from "@qivia/ui/src/designSystem/assets/Car.png";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  selectAdvancedAnalyticsVehiclesList,
  selectDriversHistoryList,
} from "../fleetSlice";
import "../../../../i18n/i18n";
import { fakeAdvancedAnalyticsVehiclesForDemo } from "../lib";
import { formatPercent } from "../../../../libSlice/materialUiFormats";
import { DriverHistoryModal } from "../../sharedModals/vehicles/driverHistoryModal";
import { MileageDeviationTag } from "./deviationComponents";
import { ContractDataSection } from "./contractDataSection";

export const AdvancedAnalyticsVehicleSidePanel: FunctionComponent = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useParams();
  const advancedAnalyticsVehicleListInit = useAppSelector(
    selectAdvancedAnalyticsVehiclesList,
  );
  const firstPath = location.pathname.split("/")[1]; // home or departments

  const isDemoEnvironment = useCallback(() => {
    return import.meta.env["VITE_TARGET_ENV"] === "demo";
  }, []);

  const [isDriverHistoryModalOpen, setIsDriverHistoryModalOpen] =
    useState(false);
  const allDriversHistoryList = useAppSelector(selectDriversHistoryList);
  const vehicle = useMemo(
    () =>
      isDemoEnvironment()
        ? [
            ...advancedAnalyticsVehicleListInit,
            ...fakeAdvancedAnalyticsVehiclesForDemo,
          ].find((v) => v.uuid === params.id)
        : advancedAnalyticsVehicleListInit.find((v) => v.uuid === params.id),
    [advancedAnalyticsVehicleListInit, isDemoEnvironment, params.id],
  );

  const driversHistoryList = useMemo(
    () => allDriversHistoryList.filter((dh) => dh.vehicleUuid === params.id),
    [allDriversHistoryList, params.id],
  );

  const lastDriver = useMemo(() => driversHistoryList[0], [driversHistoryList]);

  const handleClickDriver = useCallback(() => {
    navigate(`/${firstPath}/drivers/${lastDriver?.driverUuid}`);
  }, [lastDriver?.driverUuid, navigate, firstPath]);

  useEffect(() => {
    if (!vehicle) {
      navigate(`/${firstPath}/fleet/advanced_analytics`);
    }
  }, [navigate, vehicle, firstPath]);

  const onSidePanelClosed = useCallback(() => {
    navigate(`/${firstPath}/fleet/advanced_analytics`);
  }, [navigate, firstPath]);

  if (!vehicle) {
    return;
  }

  return (
    <SidePanelDS.SidePanel visible={true} onClose={onSidePanelClosed}>
      <SidePanelDS.Section
        verticalSpacing={2}
        horizontalSpacing={1.5}
        background={colors["colors/button/secondary/hover"]}
      >
        <StyledPanelContainer>
          <StyledContent>{vehicle.registrationNumber}</StyledContent>
          {vehicle.reference && (
            <>
              <Spacer y={0.5} />
              <Tag
                text={vehicle.reference}
                borderColor={colors["colors/borders/cells/cells"]}
                backgroundColor={
                  colors["colors/surfaces/background/background_level0"]
                }
                textColor={colors["colors/button/primary/default"]}
              />
            </>
          )}
          <StyledImg src={Car} alt="Car" />
          <Spacer y={1} />
          <StyledContent>
            {`${vehicle.marque ? vehicle.marque : ""} ${
              vehicle.modele ? vehicle.modele : ""
            }`.trim()}
          </StyledContent>
          {vehicle.energie && (
            <>
              <Spacer y={0.25} />
              <StyledEnergie>
                <TextCapitalized>
                  {vehicle.energie && t(`vehicles.energie.${vehicle.energie}`)}
                </TextCapitalized>
              </StyledEnergie>
            </>
          )}
        </StyledPanelContainer>
        <Spacer y={1.5} />
      </SidePanelDS.Section>
      <SidePanelDS.Section verticalSpacing={2} horizontalSpacing={1.5}>
        <SectionTitle>
          <TextCapitalized>{t("vehicles.sidepanel.details")}</TextCapitalized>
        </SectionTitle>
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.status")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <TextCapitalized>
              {t(`vehicles.status.${vehicle.status}`)}
            </TextCapitalized>
          }
        />
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>{t("vehicles.sidepanel.type")}</TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <Tag
              text={t(`vehicles.type.${vehicle.vehicleType}`)}
              borderColor={colors["colors/borders/cells/cells"]}
              backgroundColor={
                colors["colors/surfaces/background/background_level0"]
              }
              textColor={colors["colors/text/darkGrey"]}
            />
          }
        />
      </SidePanelDS.Section>

      <SidePanelDS.Section verticalSpacing={2} horizontalSpacing={1.5}>
        <SectionTitle>
          <TextCapitalized>
            {t("vehicles.sidepanel.driversHistory")}
          </TextCapitalized>
        </SectionTitle>
        {lastDriver && (
          <>
            <Spacer y={2} />
            <SidePanelDS.ActionBlock>
              <SidePanelDS.SectionedActionStripe
                left={
                  <StyledRowCenter>
                    <TextCapitalized>
                      <Avatar
                        size={"S"}
                        color={"orange"}
                        lastName={lastDriver.driverInfo.lastName}
                        firstName={lastDriver.driverInfo.firstName}
                      />
                    </TextCapitalized>
                    <Spacer x={0.5} />
                    {lastDriver.driverInfo.firstName.concat(
                      " ",
                      lastDriver.driverInfo.lastName,
                    )}
                  </StyledRowCenter>
                }
                right={
                  <StyledLittleIcon>
                    <RemoveRedEyeOutlined
                      key="driver"
                      onClick={handleClickDriver}
                    />
                    <Spacer x={1} />
                  </StyledLittleIcon>
                }
              />
            </SidePanelDS.ActionBlock>
            <Spacer y={1} />
            <ButtonDS
              sizeButton="S"
              format="hug"
              buttonType="secondary"
              text={t("vehicles.sidepanel.viewDriversHistoryList")}
              leftIcon={<RemoveRedEyeOutlined />}
              onClick={() => setIsDriverHistoryModalOpen(true)}
            />
            <DriverHistoryModal
              visible={isDriverHistoryModalOpen}
              onClose={() => setIsDriverHistoryModalOpen(false)}
              driversHistoryList={driversHistoryList}
            />
          </>
        )}
      </SidePanelDS.Section>

      <SidePanelDS.Section verticalSpacing={2} horizontalSpacing={1.5}>
        <SectionTitle>
          <TextCapitalized>
            {t("vehicles.sidepanel.informations")}
          </TextCapitalized>
        </SectionTitle>
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.mileage")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {!vehicle.mileage ? "-" : vehicle.mileage.toFixed(2)}
              <Spacer x={0.25} />
              <StyledSubTitle>
                <TextCapitalized>
                  {t("vehicles.sidepanel.mileage.units")}
                </TextCapitalized>
              </StyledSubTitle>
            </StyledRow>
          }
        />
        <Spacer y={0.25} />
        {vehicle.lastMileageDate && (
          <StyledSubTitle>
            <TextCapitalized>
              {t("vehicles.sidepanel.mileageDate")}
            </TextCapitalized>
            <Spacer x={0.25} />:<Spacer x={0.25} />
            {dateFormatterDayMonthYear(new Date(vehicle.lastMileageDate))}
          </StyledSubTitle>
        )}
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.projectionEndOfContract.title")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {(vehicle.projectionEndOfContract
                ? vehicle.projectionEndOfContract
                : "-") +
                " / " +
                (vehicle.contractMileage ? vehicle.contractMileage : "-")}
              <Spacer x={0.25} />
              <StyledSubTitle>
                <TextCapitalized>
                  {t("vehicles.sidepanel.projectionEndOfContract.units")}
                </TextCapitalized>
              </StyledSubTitle>
            </StyledRow>
          }
        />
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.mileageDeviation")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            vehicle.mileageDeviation !== null ? (
              <MileageDeviationTag
                status={vehicle.mileageDeviation}
                text={t(
                  `fleet.advanced_analytics.deviation.${vehicle.mileageDeviation}`,
                )}
              />
            ) : (
              "-"
            )
          }
        />
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.monthlyMileageKmVariation")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRowCentered>
              {vehicle.mileageDeviationPercentage !== null &&
              vehicle.mileageDeviation !== null ? (
                <MileageDeviationTag
                  status={vehicle.mileageDeviation}
                  text={formatPercent(vehicle.mileageDeviationPercentage)}
                />
              ) : vehicle.mileageDeviationPercentage !== null ? (
                formatPercent(vehicle.mileageDeviationPercentage)
              ) : (
                "-"
              )}
              {vehicle.monthlyMileageKmVariation !== null &&
                vehicle.mensualContractMileage !== null && (
                  <>
                    <Spacer x={0.5} />
                    {vehicle.monthlyMileageKmVariation}
                    <Spacer x={0.25} />
                    <StyledSubTitle>km</StyledSubTitle>
                    <Spacer x={0.25} />
                    {" / "}
                    <Spacer x={0.25} />
                    {vehicle.mensualContractMileage}
                    <Spacer x={0.25} />
                    <StyledSubTitle>km</StyledSubTitle>
                  </>
                )}
            </StyledRowCentered>
          }
        />
        <Spacer y={2} />
        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.consumption.title")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {!vehicle.averageConsumption ? "-" : vehicle.averageConsumption}
              <Spacer x={0.25} />
              <StyledSubTitle>
                <TextCapitalized>
                  {t("vehicles.sidepanel.consumption.units")}
                </TextCapitalized>
              </StyledSubTitle>
            </StyledRow>
          }
        />
        <StyledSubTitle>
          <TextCapitalized>
            {t("vehicles.sidepanel.consumption.subTitle")}
          </TextCapitalized>
        </StyledSubTitle>
      </SidePanelDS.Section>

      <SidePanelDS.Section verticalSpacing={2} horizontalSpacing={1.5}>
        <SectionTitle>
          <TextCapitalized>{t("vehicles.sidepanel.pollution")}</TextCapitalized>
        </SectionTitle>
        <Spacer y={2} />

        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.emission")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {vehicle.averageEmission === null ? "-" : vehicle.averageEmission}
              <Spacer x={0.25} />
              <StyledSubTitle>
                {t("vehicles.sidepanel.emission.units")}
              </StyledSubTitle>
            </StyledRow>
          }
        />
        <Spacer y={2} />

        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.energyClass.title")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {vehicle.energyClass === null ? (
                "-"
              ) : (
                <>
                  <EnergyClass
                    energyClass={vehicle.energyClass as EnergyClass}
                  />
                  <Spacer x={0.25} />
                </>
              )}
              <Spacer x={0.25} />
            </StyledRow>
          }
        />
        <Spacer y={0.25} />
        {vehicle.energyClass && (
          <StyledSubTitle>
            <TextCapitalized>
              {t(`vehicles.sidepanel.energyClass.${vehicle.energyClass}`)}
            </TextCapitalized>
            <Spacer x={0.25} />
          </StyledSubTitle>
        )}
        <Spacer y={2} />

        <SidePanelDS.SectionRow
          label={
            <SectionRowLabel>
              <TextCapitalized>
                {t("vehicles.sidepanel.critAir")}
              </TextCapitalized>
            </SectionRowLabel>
          }
          value={
            <StyledRow>
              {vehicle.critAir === null ? (
                <>
                  {"-"} <Spacer x={0.25} />
                </>
              ) : (
                <CritAir critAir={`${vehicle.critAir}`.toString() as CritAir} />
              )}
            </StyledRow>
          }
        />
      </SidePanelDS.Section>
      <ContractDataSection vehicle={vehicle} />
    </SidePanelDS.SidePanel>
  );
};

const SectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const SectionRowLabel = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]}
`;

const StyledRow = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledRowCentered = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSubTitle = styled.div`
  display: flex;
  ${typographies["Body/XXS"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledLittleIcon = styled.div`
  display: flex;
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledContent = styled.div`
  ${typographies["Header/H4"]};
`;

const StyledImg = styled.img`
  filter: grayscale(100%);
  width: 15rem;
  height: 6.5rem;
  object-fit: contain;
`;

const StyledEnergie = styled.span`
  ${typographies["Body/S"]};
  color: ${colors["colors/text/darkGrey"]};
  text-align: center;
`;
